import { LINE_STRING, POINT, POLYGON } from 'client/utils/constants/geometryType';
import {
    isPointCoordinate,
    areSamePoint,
    readGeometryCoordinates,
    exceedsBoundingExtent,
    getExtentExceeded,
    getSketchEdgeStyleVariant
} from '../validationUtils';
import { DRAW_STAGES } from '../drawStageConstants';

const normalizeRing = (geometry) => {
    const coordinates = readGeometryCoordinates(geometry);
    if (!coordinates || !Array.isArray(coordinates[0])) {
        return null;
    }

    const ring = coordinates[0].filter((point) => isPointCoordinate(point));
    if (ring.length < 4) {
        return null;
    }

    if (!areSamePoint(ring[0], ring[ring.length - 1])) {
        return null;
    }

    return ring;
};

const getDistinctVertexCount = (ring) => {
    return new Set(ring.slice(0, -1).map(([x, y]) => `${x}:${y}`)).size;
};

const hasAtLeastThreeDistinctVertices = (ring) => {
    return getDistinctVertexCount(ring) >= 3;
};

const hasAtLeastTwoDistinctVertices = (ring) => {
    return getDistinctVertexCount(ring) >= 2;
};

const hasNonCollinearVertices = (ring) => {
    const vertices = ring.slice(0, -1);
    if (vertices.length < 3) {
        return false;
    }

    let firstPoint = null;
    let secondPoint = null;

    for (const point of vertices) {
        if (!firstPoint) {
            firstPoint = point;
            continue;
        }

        if (!secondPoint && !areSamePoint(point, firstPoint)) {
            secondPoint = point;
            continue;
        }

        if (!secondPoint || areSamePoint(point, firstPoint) || areSamePoint(point, secondPoint)) {
            continue;
        }

        const crossProduct =
            ((secondPoint[0] - firstPoint[0]) * (point[1] - firstPoint[1])) -
            ((secondPoint[1] - firstPoint[1]) * (point[0] - firstPoint[0]));

        if (crossProduct !== 0) {
            return true;
        }
    }

    return false;
};

const isCompleteCustomPolygon = (geometry) => {
    const ring = normalizeRing(geometry);
    if (!ring) {
        return false;
    }

    return hasAtLeastThreeDistinctVertices(ring) && hasNonCollinearVertices(ring);
};

const hasSecondPointProgress = (geometry) => {
    const ring = normalizeRing(geometry);
    if (!ring) {
        return false;
    }

    return hasAtLeastTwoDistinctVertices(ring);
};

const hasLineSecondPointProgress = (geometry) => {
    const coordinates = readGeometryCoordinates(geometry);
    if (!coordinates || coordinates.length < 2) {
        return false;
    }

    const points = coordinates.filter((point) => isPointCoordinate(point));
    if (points.length < 2) {
        return false;
    }

    return !points.every((point) => areSamePoint(point, points[0]));
};

const customPolygonPolicy = Object.freeze({
    usesPolygonSketchCache: true,

    getStage ({ geometryType, geometry }) {
        if (geometryType === POINT) {
            return DRAW_STAGES.FIRST_POINT;
        }

        if (geometryType === LINE_STRING) {
            return DRAW_STAGES.EDGE_SKETCH;
        }

        if (geometryType === POLYGON) {
            return isCompleteCustomPolygon(geometry)
                ? DRAW_STAGES.SHAPE_SKETCH_COMPLETE
                : DRAW_STAGES.SHAPE_SKETCH_INCOMPLETE;
        }

        return DRAW_STAGES.UNKNOWN;
    },

    isComplete ({ geometryType, geometry }) {
        return geometryType === POLYGON && isCompleteCustomPolygon(geometry);
    },

    shouldNotify ({ geometryType, geometry, stage, complete }) {
        if (geometryType === POLYGON && stage === DRAW_STAGES.SHAPE_SKETCH_COMPLETE) {
            return true;
        }

        if (geometryType === POLYGON && hasSecondPointProgress(geometry)) {
            return true;
        }

        if (geometryType === LINE_STRING && stage === DRAW_STAGES.EDGE_SKETCH && hasLineSecondPointProgress(geometry)) {
            return true;
        }

        if (!complete) {
            return false;
        }

        return false;
    },

    getExtentMessageDescriptor () {
        return {
            id: 'mapPage.error.areaTooLarge',
            values: undefined
        };
    },

    getExtentExceeded ({ geometryType, geometry, userLimit }) {
        if (geometryType === LINE_STRING) {
            return exceedsBoundingExtent(geometry, userLimit);
        }

        return getExtentExceeded({ geometry, userLimit });
    },

    getStyleVariant (context) {
        return getSketchEdgeStyleVariant(context);
    },

    shouldSkipExtentCheck ({ geometryType, isSketchEdgeGeometry, geometry }) {
        // For sketch edges, only skip extent check if there's not enough second point progress
        if (geometryType === LINE_STRING && isSketchEdgeGeometry) {
            return !hasLineSecondPointProgress(geometry);
        }
        return false;
    },

    shouldValidateGeometry ({ complete, isSketchEdgeGeometry }) {
        // Never validate sketch edges for self-intersection, only extent
        if (isSketchEdgeGeometry) {
            return false;
        }
        // For complete geometry (not sketch), validate self-intersection
        if (complete) {
            return true;
        }
        return false;
    }
});

export default customPolygonPolicy;

// Exported for targeted unit testing of complex helpers
export {
    normalizeRing,
    isCompleteCustomPolygon,
    hasSecondPointProgress,
    hasLineSecondPointProgress,
    hasNonCollinearVertices
};
